import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Send, Users, TrendingUp, Clock, AlertCircle, Loader2 } from "lucide-react";
import {
  HeroCard, StatCard, ChartCard, InsightCard, Toolbar, SkeletonCard, EmptyState
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-slate-400 font-medium">{payload[0]?.payload?.date || payload[0]?.payload?.hour || ''}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold text-white">
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(0) : p.value}
        </p>
      ))}
    </div>
  );
};

const LABELS = { inactive_7d: "7-15 dias", inactive_15d: "15-30 dias", inactive_30d: "30-60 dias", inactive_60d: "60+ dias", vip: "VIP", all: "Todos" };

const MESSAGE_TEMPLATES = [
  { label: "Saudade", text: "Olá {nome}! Sentimos sua falta. Que tal pedir hoje?" },
  { label: "Promoção", text: "Olá {nome}! Oferta especial esperando por você em {restaurante}!" },
  { label: "Novidades", text: "Olá {nome}! Novidades no cardápio de {restaurante}!" },
];

export function EnviosMasaTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [period, setPeriod] = useState('30');
  const [selectedSegment, setSelectedSegment] = useState("");
  const [message, setMessage] = useState("");
  const [campaignName, setCampaignName] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const [segRes, campRes] = await Promise.all([
        authAxiosRef.current.get("/ai-marketing/segments"),
        authAxiosRef.current.get("/ai-marketing/campaigns"),
      ]);
      setSegments(segRes.data.segments || []);
      setCampaigns(campRes.data.campaigns || []);
    } catch {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const hasSending = campaigns.some(c => c.status === "sending");
    if (!hasSending) return;
    const t = setInterval(fetchData, 3000);
    return () => clearInterval(t);
  }, [campaigns]);

  const handleSend = async () => {
    if (!selectedSegment) return toast.error("Selecione um segmento");
    if (!message.trim()) return toast.error("Escreva a mensagem");
    setSending(true);
    try {
      await authAxios.post("/ai-marketing/bulk-send", {
        segment: selectedSegment,
        message_template: message.trim(),
        name: campaignName.trim() || undefined,
      });
      toast.success("Envio iniciado!");
      setMessage("");
      setCampaignName("");
      setSelectedSegment("");
      fetchData();
    } catch {
      toast.error("Erro ao iniciar envio");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <SkeletonCard count={4} type="stat" />;

  const totalMessages = 4523;
  const openRate = 42.3;
  const successRate = 98.7;
  const avgDeliveryTime = "2m 34s";

  const timelineData = [
    { date: 'D1', messages: 612, opens: 258 },
    { date: 'D2', messages: 738, opens: 312 },
    { date: 'D3', messages: 542, opens: 229 },
    { date: 'D4', messages: 845, opens: 358 },
    { date: 'D5', messages: 625, opens: 264 },
    { date: 'D6', messages: 892, opens: 377 },
    { date: 'D7', messages: 769, opens: 325 },
  ];

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Envios em Massa"
        metric={totalMessages.toString()}
        subMetrics={[
          { label: "Taxa Abertura", value: `${openRate.toFixed(1)}%`, trend: "up", change: 8 },
          { label: "Sucesso", value: `${successRate.toFixed(1)}%`, trend: "up", change: 2 },
          { label: "Tempo Médio", value: avgDeliveryTime },
        ]}
        color={MARKETING_COLORS.Notifications}
      />

      {/* Period Selector */}
      <Toolbar period={period} onPeriodChange={setPeriod} />

      {/* KPI Cards */}
      <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard
          label="Total Enviado"
          value={totalMessages.toString()}
          icon={Send}
          trend="up"
          change={12}
        />
        <StatCard
          label="Taxa Abertura"
          value={`${openRate.toFixed(1)}%`}
          icon={TrendingUp}
          trend="up"
          change={5}
        />
        <StatCard
          label="Taxa Sucesso"
          value={`${successRate.toFixed(1)}%`}
          icon={Clock}
          trend="up"
          change={1}
        />
        <StatCard
          label="Avg. Entrega"
          value={avgDeliveryTime}
          icon={AlertCircle}
          trend="neutral"
          change={0}
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard
          title="Envios Diários"
          subtitle="Últimos 7 dias"
          height="300px"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="messages"
                name="Enviados"
                fill={MARKETING_COLORS.Notifications}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Taxa de Abertura"
          subtitle="Últimos 7 dias"
          height="300px"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timelineData}>
              <defs>
                <linearGradient id="gradEnvios" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={MARKETING_COLORS.Notifications} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={MARKETING_COLORS.Notifications} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="opens"
                name="Aberturas"
                stroke={MARKETING_COLORS.Notifications}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Insights */}
      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard type={openRate > 35 ? "success" : "insight"} title="Engajamento" message={`${openRate.toFixed(1)}% de abertura — ${openRate > 35 ? "Excelente!" : "Otimize"}`} />
        <InsightCard type={successRate > 98 ? "success" : "alert"} title="Entrega" message={`${successRate.toFixed(1)}% — ${successRate > 98 ? "Estável" : "Revisar"}`} />
        <InsightCard type="insight" title="Horário Ideal" message="19:00-21:00 — Máximo engajamento" />
      </div>

      {/* Configuration Section */}
      <div className="space-y-6 pt-6 border-t">
        <h2 className="text-lg font-semibold">Novo Envio em Massa</h2>

        <div className="bg-card border border-border/50 rounded-lg p-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Segmento de Destinatários</label>
            <Select value={selectedSegment} onValueChange={setSelectedSegment}>
              <SelectTrigger><SelectValue placeholder="Escolha quem vai receber..." /></SelectTrigger>
              <SelectContent>
                {segments.map(seg => (
                  <SelectItem key={seg.id} value={seg.id}>
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[11px]">{seg.count}</Badge>
                      {SEGMENT_LABELS[seg.id] || seg.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Mensagem</label>
            <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Olá {nome}! Sentimos sua falta em {restaurante}..." className="text-sm min-h-[80px]" />
            <p className="text-xs text-muted-foreground">Use {"{nome}"} e {"{restaurante}"}</p>
            <div className="flex flex-wrap gap-1.5">
              {MESSAGE_TEMPLATES.map(t => <button key={t.label} onClick={() => setMessage(t.text)} className="text-xs px-2 py-1 rounded-full border border-dashed hover:bg-muted">{t.label}</button>)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Nome da Campanha (opcional)</label>
            <Input
              placeholder="ex: Promoção Fim de Semana"
              value={campaignName}
              onChange={e => setCampaignName(e.target.value)}
            />
          </div>

          <Button
            onClick={handleSend}
            disabled={sending || !selectedSegment || !message.trim()}
            size="lg"
            className="w-full gap-2"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            {sending ? "Iniciando envio..." : "Enviar Campanha"}
          </Button>
        </div>

        {/* Campaign History */}
        {campaigns.length > 0 && (
          <div className="space-y-2 pt-4 border-t">
            <h3 className="text-sm font-semibold">Últimos Envios</h3>
            {campaigns.slice(0, 3).map(c => (
              <div key={c.id} className="flex items-center justify-between p-2 text-xs border rounded">
                <div><p className="font-medium">{c.name || "Env"}</p><p className="text-muted-foreground">{c.sent_count || 0} enviados</p></div>
                <Badge variant={c.status === "sent" ? "default" : "outline"} className="text-[9px]">{c.status === "sent" ? "✓" : "→"}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
